import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  FaArrowRight,
  FaCar,
  FaCheckCircle,
  FaHeadset,
  FaIdCard,
  FaKey,
  FaMapMarkerAlt,
  FaShieldAlt,
  FaGasPump,
} from "react-icons/fa";
import CarSearchWidget from "../helpers/CarSearchWidget";
import { carBrands, findCarBrandBySlug } from "../../data/cars";

const buildHeadings = (brand) => [
  `Why rent with ${brand.name}?`,
  `How to book ${brand.name} cars`,
  `Manage your ${brand.name} booking`,
  "Insurance & Damage Waivers",
  "Mileage limitations",
  "Toll management",
  "Fuel policies",
  "Age & Driver requirements",
  "Popular pick-up locations",
];

const popularLocations = [
  "Los Angeles Int'l (LAX)",
  "Miami Int'l (MIA)",
  "Orlando Int'l (MCO)",
  "Las Vegas Harry Reid (LAS)",
  "Denver Int'l (DEN)",
  "Atlanta Hartsfield (ATL)",
];

export default function CarDetail() {
  const { carSlug } = useParams();
  const brand = findCarBrandBySlug(carSlug);

  if (!brand) {
    return <Navigate to="/cars" replace />;
  }

  const headings = buildHeadings(brand);
  const otherBrands = carBrands
    .filter((item) => item.slug !== brand.slug)
    .slice(0, 8);

  return (
    <div className="bg-[#f8f9fc] font-sans">
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(40,113,211,0.45),transparent_36%),linear-gradient(135deg,#0f172a,#1d4ed8)]" />
        <div className="relative mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
          <nav className="mb-6 flex flex-wrap items-center gap-2 text-sm font-bold text-blue-100">
            <Link to="/" className="transition hover:text-white">
              Home
            </Link>
            <FaArrowRight className="h-3 w-3" />
            <Link to="/cars" className="transition hover:text-white">
              Car Rentals
            </Link>
            <FaArrowRight className="h-3 w-3" />
            <span className="text-white">{brand.name}</span>
          </nav>

          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_280px] lg:items-end">
            <div>
              <p className="mb-4 inline-flex rounded-full bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-blue-100 ring-1 ring-white/15">
                Rental Partner Guide
              </p>
              <h1 className="max-w-4xl text-4xl font-black leading-tight tracking-tight sm:text-6xl">
                Book {brand.name} Rentals
              </h1>
              <p className="mt-5 max-w-3xl text-base font-medium leading-8 text-blue-50 sm:text-lg">
                {brand.summary} Search live availability, compare vehicle
                classes, and get booking support for your next road trip.
              </p>
            </div>
            <div className="rounded-[1.5rem] border border-white/15 bg-white/10 p-6 backdrop-blur-xl">
              <div className="text-sm font-bold uppercase tracking-[0.18em] text-blue-100">
                Partner Code
              </div>
              <div className="mt-2 text-5xl font-black">{brand.code}</div>
              <div className="mt-4 inline-block rounded-full bg-white px-4 py-2 text-sm font-black text-[#2871d3]">
                {brand.region}
              </div>
            </div>
          </div>
        </div>
      </section>

      <CarSearchWidget />

      <section className="mx-auto grid max-w-7xl gap-6 px-4 py-12 sm:px-6 lg:grid-cols-[260px_minmax(0,1fr)_280px] lg:px-8">
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-[0_16px_45px_rgba(15,23,42,0.06)]">
            <h2 className="mb-4 text-sm font-black uppercase tracking-[0.18em] text-slate-400">
              Contents
            </h2>
            <div className="space-y-2">
              {headings.map((heading, index) => (
                <a
                  key={heading}
                  href={`#section-${index + 1}`}
                  className="block rounded-xl px-3 py-2 text-sm font-bold text-slate-600 transition hover:bg-blue-50 hover:text-[#2871d3]"
                >
                  {heading}
                </a>
              ))}
            </div>
          </div>
        </aside>

        <main className="min-w-0 space-y-6">
          <article className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-[0_16px_45px_rgba(15,23,42,0.06)] sm:p-8">
            <h2 className="text-3xl font-black tracking-tight text-slate-950">
              {brand.name} car rental guide
            </h2>
            <p className="mt-4 text-base font-medium leading-8 text-slate-600">
              {brand.name} rentals can be searched directly on our platform.
              Choose your pick-up and drop-off locations, travel dates, and
              vehicle class, then compare available rates in one place. Our
              booking flow is designed for quick comparison, transparent
              pricing, and support when your travel plans change.
            </p>
          </article>

          {headings.map((heading, index) => (
            <section
              key={heading}
              id={`section-${index + 1}`}
              className="scroll-mt-28 rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
            >
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-[#2871d3]">
                {index % 5 === 0 && <FaCar />}
                {index % 5 === 1 && <FaKey />}
                {index % 5 === 2 && <FaShieldAlt />}
                {index % 5 === 3 && <FaGasPump />}
                {index % 5 === 4 && <FaHeadset />}
              </div>
              <h2 className="text-2xl font-black tracking-tight text-slate-950">
                {heading}
              </h2>
              <p className="mt-4 text-sm font-medium leading-7 text-slate-600 sm:text-base">
                We help travelers compare {brand.name} options alongside other
                rental companies. Review vehicle capacities, insurance add-ons,
                mileage limits, and toll policies before confirming your
                reservation. For urgent help, call our booking advisors for live
                support and guidance.
              </p>

              {heading === "Popular pick-up locations" && (
                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  {popularLocations.map((route) => (
                    <div
                      key={route}
                      className="flex items-center gap-3 rounded-2xl bg-[#f8f9fc] p-4 text-sm font-black text-slate-700"
                    >
                      <FaMapMarkerAlt className="text-[#2871d3]" />
                      {route}
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}

          <section className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <h2 className="text-2xl font-black tracking-tight text-slate-950">
              Quick answers
            </h2>
            <div className="mt-5 space-y-3">
              {[
                `How do I find cheap ${brand.name} rentals?`,
                `Can I modify my ${brand.name} reservation?`,
                `Are underage driver fees included?`,
              ].map((question) => (
                <details
                  key={question}
                  className="rounded-2xl border border-slate-200 bg-[#f8f9fc] p-4"
                >
                  <summary className="cursor-pointer text-sm font-black text-slate-950">
                    {question}
                  </summary>
                  <p className="mt-3 text-sm font-medium leading-6 text-slate-600">
                    Use the search form on this page to compare live
                    availability and review the rate details shown in the
                    results. Policies vary by location and vehicle class, so
                    always check the selected offer before booking.
                  </p>
                </details>
              ))}
            </div>
          </section>
        </main>

        <aside className="space-y-5">
          <div className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-[0_16px_45px_rgba(15,23,42,0.06)]">
            <h2 className="text-lg font-black text-slate-950">
              Need help booking?
            </h2>
            <p className="mt-2 text-sm font-medium leading-6 text-slate-500">
              Speak with a travel advisor for vehicle and rate assistance.
            </p>
            <a
              href="tel:+18889771997"
              className="mt-5 flex w-full items-center justify-center rounded-full bg-[#2871d3] px-5 py-3 text-sm font-black text-white shadow-lg shadow-blue-600/20 transition hover:bg-[#155bc2]"
            >
              Call 1-888-977-1997
            </a>
          </div>

          {otherBrands.length > 0 && (
            <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="mb-4 text-lg font-black text-slate-950">
                Other popular brands
              </h2>
              <div className="space-y-2">
                {otherBrands.map((item) => (
                  <Link
                    key={item.slug}
                    to={`/cars/${item.slug}`}
                    className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-[#f8f9fc] p-3 transition hover:border-blue-100 hover:bg-blue-50"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-sm font-black text-[#2871d3]">
                      {item.code}
                    </span>
                    <span className="truncate text-sm font-black text-slate-700">
                      {item.name}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <FaIdCard className="shrink-0 text-[#2871d3]" />
              <span className="text-sm font-black text-slate-950">
                Age and driver requirements apply.
              </span>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <FaCheckCircle className="shrink-0 text-emerald-500" />
              <span className="text-sm font-black text-slate-950">
                Review rental conditions before checkout.
              </span>
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}
